import { c as _c } from "react/compiler-runtime";
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { Progress, Button } from '@mantine/core';
import { uri } from 'shared/uri';
import { z } from 'zod';
import { okOrThrow } from 'shared/promise';
import { appVersion } from './version';
const UpdateNotification = t0 => {
  const $ = _c(12);
  const {
    onClose,
    duration: t1
  } = t0;
  const duration = t1 === undefined ? 10000 : t1;
  const [progress, setProgress] = useState(0);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = Date.now();
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const startTime = useRef(t2);
  const updateInterval = useRef(null);
  let t3;
  let t4;
  if ($[1] !== duration) {
    t3 = () => {
      updateInterval.current = setInterval(() => {
        const elapsed = Date.now() - startTime.current;
        const progress_0 = Math.min(100, elapsed / duration * 100);
        setProgress(progress_0);
      }, 100);
      const timeout = setTimeout(() => {
        if (updateInterval.current) {
          clearInterval(updateInterval.current);
        }
        window.location.reload();
      }, duration);
      return () => {
        if (updateInterval.current) {
          clearInterval(updateInterval.current);
        }
        clearTimeout(timeout);
      };
    };
    t4 = [duration];
    $[1] = duration;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  const t5 = Math.ceil(duration / 1000);
  let t6;
  if ($[4] !== progress) {
    t6 = <Progress value={progress} size="sm" mt="xs" />;
    $[4] = progress;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== onClose) {
    t7 = <Button variant="outline" className="mt-2" onClick={() => {
      onClose();
    }}>Don't reload</Button>;
    $[6] = onClose;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t5 || $[9] !== t6 || $[10] !== t7) {
    t8 = <>A new version is available. Refreshing in {t5} seconds...{t6}{t7}</>;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  return t8;
};

/** use from the root of the SPA  */
export const useVersionCheck = ({
  queryClient
}: {
  queryClient: QueryClient;
}) => {
  const hasDismissedUpdate = useRef(false);
  const {
    data: serverVersion
  } = useQuery({
    queryKey: ['version'],
    queryFn: () => uri('version.json').getJson(z.object({
      version: z.string()
    }), {
      cache: 'no-store'
    }).then(okOrThrow).then(r => r.version),
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
    enabled: !hasDismissedUpdate.current
  }, queryClient);
  useEffect(() => {
    if (serverVersion == null || appVersion == serverVersion) return;
    let notificationId: string;
    const showNotification = () => {
      notificationId = notifications.show({
        title: 'Update Available',
        message: <UpdateNotification onClose={() => {
          hasDismissedUpdate.current = true;
          notifications.hide(notificationId);
        }} />,
        color: 'blue',
        autoClose: false,
        withCloseButton: true
      });
    };
    showNotification();
    return () => {
      if (notificationId) notifications.hide(notificationId);
    };
  }, [serverVersion]);
};